import reduce from 'lodash/reduce'
import map from 'lodash/map'

const namespaces = {
  xmlns: 'http://www.w3.org/2000/xmlns/',
  svg: 'http://www.w3.org/2000/svg',
}

const doctype =
  '<?xml version="1.0" standalone="no"?><!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">'

/**
 * Compute styles from a source SVG DOM node and copy it to a target SVG DOM node
 * @param {Node} target target SVG DOM node
 * @param {Node} source source SVG DOM node
 */
const computeAndCopyStyles = (target, source) => {
  // Create an empty SVG node to get a CSSStyleDeclaration instance with default styles
  const emptySvg = document.createElementNS(namespaces.svg, 'svg')
  document.body.appendChild(emptySvg)
  const defaultStyle = getComputedStyle(emptySvg)

  const computeAndCopyStyle = (target, source) => {
    const sourceStyle = getComputedStyle(source)
    const targetStyle = reduce(
      sourceStyle,
      (acc, property) => {
        // CSSStyleDeclaration is really a strange object…
        const value = sourceStyle.getPropertyValue(property)
        if (value !== defaultStyle.getPropertyValue(property)) {
          // keep style only if different from default
          acc[property] = value
        }
        return acc
      },
      {}
    )
    target.setAttribute('style', map(targetStyle, (value, property) => property + ':' + value).join(';'))
  }

  const sourceElements = Array.prototype.concat.apply([source], source.getElementsByTagName('*'))
  const targetElements = Array.prototype.concat.apply([target], target.getElementsByTagName('*'))

  sourceElements.forEach((sourceElement, index) => computeAndCopyStyle(targetElements[index], sourceElement))

  // No need for empty SVG anymore
  document.body.removeChild(emptySvg)
}

/**
 * Generate a downloadable SVG DOM node, with embedded styles
 * @param {Node} svg
 * @return {Node} downloadable SVG DOM node
 */
const getDownloadableSvg = svg => {
  const result = svg.cloneNode(true) // copy to keep the original intact

  result.setAttribute('version', '1.1')
  result.setAttributeNS(namespaces.xmlns, 'xmlns', namespaces.svg)

  computeAndCopyStyles(result, svg)
  return result
}

/**
 * Convert a SVG DOM node to a string
 * @param {Node} svgNode SVG DOM node
 * @return {string} Serialized SVG with styles
 */
const getSvgString = svgNode => {
  const copyNode = getDownloadableSvg(svgNode)

  const background = document.createElementNS(namespaces.svg, 'rect') // workaround to transparent background
  background.setAttribute('width', '100%')
  background.setAttribute('height', '100%')
  background.setAttribute('style', 'fill:white')
  copyNode.insertBefore(background, copyNode.firstChild)

  return doctype + new XMLSerializer().serializeToString(copyNode)
}
/**
 * Convert a SVG string to a PNG
 * @param {string} svgString
 * @param {number} width width of the resulting image
 * @param {number} height height of the resulting image
 * @return {Promise<Blob>}
 */
const svgStringToPng = (svgString, width, height) => {
  // Convert SVG string to data URL (see http://ecmanaut.blogspot.com/2006/07/encoding-decoding-utf8-in-javascript.html)
  const src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)))

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  canvas.width = width
  canvas.height = height

  const image = new Image()

  const loadImage = () => {
    context.clearRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    return new Promise(resolve => canvas.toBlob(blob => resolve(blob)))
  }

  const result = new Promise(resolve => (image.onload = () => resolve(loadImage())))

  image.src = src

  return result
}

/**
 * Generate a downloadable SVG and trigger download
 * @param {Node} svg SVG DOM node to export
 * @return {Promise<Blob>}
 */
export const exportToSvg = svg => {
  const copy = getSvgString(svg)

  return Promise.resolve(new Blob([copy], { type: 'text/xml' }))
}

const printWidth = 1920
const printHeight = 1080
const resizeDuration = 1000

/**
 * Generate a PNG from the given chart and trigger download
 * This method will find the svg node, set a fixed size to its parent and export to png after the resize
 * @return {Promise<Blob>}
 */
export const exportToPng = parent =>
  new Promise((resolve, reject) => {
    if (parent) {
      // store computed dimensions
      const rect = parent.getBoundingClientRect()
      // store original style
      const { width, height } = parent.style
      // temporary replace parent by a copy
      const copy = parent.cloneNode(true)
      parent.replaceWith(copy)
      // apply fixed dimensions and put parent at the end of the body
      parent.style.width = `${printWidth}px`
      parent.style.height = `${printHeight}px`
      document.body.appendChild(parent)
      // find svg node
      const svg = parent.querySelector('svg')
      // wait until fully resized
      setTimeout(() => {
        // get svg string
        const svgString = getSvgString(svg)
        // set back original dimensions
        parent.style.width = `${rect.width}px`
        parent.style.height = `${rect.height}px`
        setTimeout(() => {
          // replace copy by original parent
          copy.replaceWith(parent)
          // set back original style
          parent.style.width = width
          parent.style.height = height
          // resolve png
          resolve(svgStringToPng(svgString, printWidth, printHeight))
        }, resizeDuration)
      }, resizeDuration)
    } else {
      reject()
    }
  })
